import "./NewFoodstuffModal.css";
import "../ImageSelector/ImageSelector.css";
import "bootstrap/dist/css/bootstrap.min.css";

import React, { useContext, useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import PropTypes from "prop-types";
import { Modal } from "react-bootstrap";

import DefaultButton from "../DefaultButton/DefaultButton";
import ExportImageIcon from "../../assets/imgs/export-image-icon.svg";
import EditIcon from "../../assets/imgs/edit-icon.svg";
import CloseIconGray from "../../assets/imgs/close-icon-gray.svg";
import foodstuffService from "../../services/foodstuff-service";
import { mapNutritionalTableToFormState } from "../../utils/nutritional-table-mapper";
import { GlobalContext } from "../../pages/GlobalContext";
import { useIsMobile } from "../../hooks/useMediaQuery";

const RECOGNITION_DRAFT_KEY = "foodstuffRecognitionDraft";

const appendQueryParams = (baseUrl, extraQueryParams) => {
  if (!extraQueryParams) {
    return baseUrl;
  }

  const separator = baseUrl.includes("?") ? "&" : "?";
  return `${baseUrl}${separator}${extraQueryParams}`;
};

const NewFoodstuffModal = ({ show, onClose, onManualCreate, extraQueryParams, suggestion }) => {
  const isMobile = useIsMobile();
  const cameraInputRef = useRef(null);
  const [step, setStep] = useState("choice");
  const [uploadedImage, setUploadedImage] = useState(null);
  const [recognitionError, setRecognitionError] = useState("");
  const [suggestionPhotos, setSuggestionPhotos] = useState([]);
  const [suggestionPhotosError, setSuggestionPhotosError] = useState("");
  const { setIsLoading } = useContext(GlobalContext);

  const hasSuggestionImages = Boolean(suggestion?.has_images);

  const revokeLocalPreview = (image) => {
    if (image?.file && image?.preview) {
      URL.revokeObjectURL(image.preview);
    }
  };

  const handleImageSelected = (file) => {
    if (!file) {
      return;
    }

    setUploadedImage((current) => {
      revokeLocalPreview(current);

      return {
        file,
        preview: URL.createObjectURL(file),
        suggestionPhotoId: null,
      };
    });
    setRecognitionError("");
    setStep("upload");
  };

  const handleSelectSuggestionPhoto = (photo) => {
    if (!photo?.id || !photo?.url) {
      return;
    }

    setUploadedImage((current) => {
      if (current?.suggestionPhotoId === photo.id) {
        revokeLocalPreview(current);
        return null;
      }

      revokeLocalPreview(current);

      return {
        file: null,
        preview: photo.url,
        suggestionPhotoId: photo.id,
      };
    });
    setRecognitionError("");
  };

  const clearUploadedImage = () => {
    setUploadedImage((current) => {
      revokeLocalPreview(current);
      return null;
    });
    setRecognitionError("");
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: "image/*",
    maxFiles: 1,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        handleImageSelected(acceptedFiles[0]);
      }
    },
  });

  const resetState = () => {
    setStep("choice");
    clearUploadedImage();
    setRecognitionError("");
    setSuggestionPhotos([]);
    setSuggestionPhotosError("");
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleOpenCamera = () => {
    cameraInputRef.current?.click();
  };

  const handleCameraInputChange = (event) => {
    handleImageSelected(event.target.files?.[0]);
    event.target.value = "";
  };

  useEffect(() => {
    if (!show) {
      resetState();
      return;
    }

    if (!suggestion?.id || !suggestion?.has_images) {
      setSuggestionPhotos([]);
      return;
    }

    let cancelled = false;

    const loadSuggestionPhotos = async () => {
      setSuggestionPhotosError("");
      try {
        const detail = await foodstuffService.getFoodstuffSuggestionById(suggestion.id);
        if (cancelled) {
          return;
        }

        setSuggestionPhotos(detail?.photos || []);
      } catch (error) {
        if (!cancelled) {
          setSuggestionPhotosError("Não foi possível carregar as imagens da solicitação.");
        }
      }
    };

    loadSuggestionPhotos();

    return () => {
      cancelled = true;
    };
  }, [show, suggestion?.id, suggestion?.has_images]);

  const handleManualCreate = () => {
    handleClose();
    if (onManualCreate) {
      onManualCreate();
      return;
    }
    window.location.href = appendQueryParams("/create-foodstuff", extraQueryParams);
  };

  const applyRecognitionAndRedirect = (response) => {
    const mappedState = mapNutritionalTableToFormState(response);

    if (!mappedState) {
      setRecognitionError(
        "Não foi possível identificar os dados na imagem. Tente outra foto ou cadastre manualmente."
      );
      return false;
    }

    sessionStorage.setItem(RECOGNITION_DRAFT_KEY, JSON.stringify(mappedState));
    window.location.href = appendQueryParams(
      "/create-foodstuff?fromRecognition=true",
      extraQueryParams
    );
    return true;
  };

  const handleAnalyzeNutritionalTable = async () => {
    const hasUploadedFile = Boolean(uploadedImage?.file);
    const hasSuggestionPhoto = Boolean(uploadedImage?.suggestionPhotoId && suggestion?.id);

    if (!hasUploadedFile && !hasSuggestionPhoto) {
      setRecognitionError("Selecione uma imagem para continuar.");
      return;
    }

    setIsLoading(true);
    setRecognitionError("");

    try {
      const response = hasSuggestionPhoto
        ? await foodstuffService.recognizeSuggestionPhoto(
            suggestion.id,
            uploadedImage.suggestionPhotoId
          )
        : await foodstuffService.recognizeNutritionalTable(uploadedImage.file);

      applyRecognitionAndRedirect(response);
    } catch (error) {
      setRecognitionError(
        hasSuggestionPhoto
          ? "Erro ao analisar a imagem do solicitante. Tente novamente."
          : "Erro ao analisar a imagem. Verifique a foto e tente novamente."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const hasSelectedImage = Boolean(
    uploadedImage?.file || uploadedImage?.suggestionPhotoId
  );

  const handleBackToChoice = () => {
    setStep("choice");
    clearUploadedImage();
    setRecognitionError("");
  };

  const modalTitle = "Novo alimento";
  const modalDescription =
    step === "choice"
      ? "Como você deseja cadastrar o alimento?"
      : "Envie ou tire uma foto nítida da embalagem ou da tabela nutricional.";

  const uploadPlaceholderText = isMobile
    ? "Toque para escolher da galeria"
    : "Arraste uma imagem ou clique para selecionar";

  const imageOptionHint = hasSuggestionImages
    ? "Envie uma foto e preencha automaticamente ou use uma foto do solicitante"
    : "Envie uma foto e preencha automaticamente";

  return (
    <div className="new-foodstuff-modal">
      <Modal
        show={show}
        onHide={handleClose}
        onExit={handleClose}
        dialogClassName="image-selector-modal__dialog"
        contentClassName="image-selector-modal"
        centered
      >
        <div className="image-creator__modal__header">
          <div className="image-creator__modal__header__info">
            <h4>{modalTitle}</h4>
          </div>
          <a
            className="image-creator__modal__header__close"
            href="javascript:;"
            onClick={handleClose}
          >
            <img src={CloseIconGray} alt="Fechar" />
          </a>
        </div>
        <div className="image-creator__modal__content">
          {modalDescription && (
            <p className="new-foodstuff-modal__description">{modalDescription}</p>
          )}

          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="new-foodstuff-modal__camera-input"
            onChange={handleCameraInputChange}
          />

          {step === "choice" && (
            <div className="new-foodstuff-modal__choice">
              <button
                type="button"
                className="new-foodstuff-modal__choice__option"
                onClick={handleManualCreate}
              >
                <span className="new-foodstuff-modal__choice__option__icon" aria-hidden="true">
                  <img src={EditIcon} alt="" />
                </span>
                <span className="new-foodstuff-modal__choice__option__text">
                  <span className="new-foodstuff-modal__choice__option__title">Criar manualmente</span>
                  <span className="new-foodstuff-modal__choice__option__hint">
                    Preencha todos os campos do formulário
                  </span>
                </span>
              </button>
              <button
                type="button"
                className="new-foodstuff-modal__choice__option"
                onClick={() => setStep("upload")}
              >
                <span
                  className="new-foodstuff-modal__choice__option__icon new-foodstuff-modal__choice__option__icon--export"
                  aria-hidden="true"
                >
                  <img src={ExportImageIcon} alt="" />
                </span>
                <span className="new-foodstuff-modal__choice__option__text">
                  <span className="new-foodstuff-modal__choice__option__title">
                    A partir de uma imagem
                  </span>
                  <span className="new-foodstuff-modal__choice__option__hint">
                    {imageOptionHint}
                  </span>
                </span>
              </button>
            </div>
          )}

          {step === "upload" && (
            <div className="new-foodstuff-modal__upload">
              {hasSuggestionImages && (
                <div className="new-foodstuff-modal__suggestion-photos">
                  <p className="new-foodstuff-modal__suggestion-photos__title">
                    Imagens do solicitante
                  </p>
                  {suggestionPhotosError ? (
                    <p className="new-foodstuff-modal__error">{suggestionPhotosError}</p>
                  ) : (
                    <div className="new-foodstuff-modal__suggestion-photos__list">
                      {suggestionPhotos.map((photo) => (
                        <button
                          key={photo.id}
                          type="button"
                          className={`new-foodstuff-modal__suggestion-photos__item ${
                            uploadedImage?.suggestionPhotoId === photo.id
                              ? "new-foodstuff-modal__suggestion-photos__item--selected"
                              : ""
                          }`}
                          onClick={() => handleSelectSuggestionPhoto(photo)}
                        >
                          <img src={photo.url} alt="Imagem da solicitação" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {uploadedImage?.preview ? (
                <div className="new-foodstuff-modal__preview-field">
                  <button
                    type="button"
                    className="new-foodstuff-modal__preview-field__remove"
                    onClick={clearUploadedImage}
                    aria-label="Remover imagem selecionada"
                  >
                    <img src={CloseIconGray} alt="" />
                  </button>
                  <img
                    src={uploadedImage.preview}
                    alt="Preview da imagem"
                    className="new-foodstuff-modal__preview"
                  />
                </div>
              ) : (
                <>
                  <div
                    {...getRootProps()}
                    className={`image-creator__modal__dropzone ${isDragActive ? "image-creator__modal__dropzone--active" : ""}`}
                  >
                    <input {...getInputProps()} />
                    <div className="image-creator__modal__upload">
                      <div className="image-creator__modal__upload__icon">
                        <img src={ExportImageIcon} alt="Enviar imagem" />
                      </div>
                      <div className="image-creator__modal__upload__title">
                        <p>{uploadPlaceholderText}</p>
                      </div>
                    </div>
                  </div>
                  {isMobile && (
                    <button
                      type="button"
                      className="new-foodstuff-modal__retake-photo"
                      onClick={handleOpenCamera}
                    >
                      Tirar foto
                    </button>
                  )}
                </>
              )}

              {recognitionError && (
                <p className="new-foodstuff-modal__error">{recognitionError}</p>
              )}
              <div className="image-creator__modal__buttons">
                <div className="image-creator__modal__button">
                  <DefaultButton
                    title="Voltar"
                    type="secondary"
                    onClick={handleBackToChoice}
                  />
                </div>
                <div className="image-creator__modal__button">
                  <DefaultButton
                    title="Analisar imagem"
                    onClick={handleAnalyzeNutritionalTable}
                    disabled={!hasSelectedImage}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

NewFoodstuffModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onManualCreate: PropTypes.func,
  extraQueryParams: PropTypes.string,
  suggestion: PropTypes.shape({
    id: PropTypes.number,
    has_images: PropTypes.bool,
    foodstuff_name: PropTypes.string,
  }),
};

export { RECOGNITION_DRAFT_KEY };
export default NewFoodstuffModal;
